import { createContext, type ComponentChildren } from "preact";
import { useContext, useEffect, useMemo, useState } from "preact/hooks";
import { useTranslation } from "react-i18next";
import {
  MessagingService,
  type MessagePayload,
} from "../components/checkoutForm/services";
import type {
  ButtonStyle,
  CheckoutStyle,
  InputStyle,
  LoaderStyle,
} from "./types/styles";
import { safeJsonParse } from "../utils/safeJsonParse";
import { useListenToResultStep } from "../hooks/useListenToResultStep";
import { safeLocalStorage } from "../services/utils";

type SDKContextType = {
  messagingService: MessagingService<MessagePayload[]>;
  styles?: {
    submitButton?: ButtonStyle;
    input?: InputStyle;
    loader?: LoaderStyle;
    checkout?: CheckoutStyle;
  };
  sdkHeadlessMode: boolean;
  isEmbedBySDK: boolean;
  isUsingDimensionChangeBySDK: boolean;
};

const SDKContext = createContext<SDKContextType | null>(null);

const styleQueryParam = new URLSearchParams(window.location.search).get(
  "style"
);

export default function SDKContextProvider({
  children,
}: {
  children: ComponentChildren;
}) {
  const [styles, setStyles] = useState<SDKContextType["styles"]>(() =>
    styleQueryParam ? safeJsonParse(atob(styleQueryParam)) : undefined
  );
  const [sdkHeadlessMode, setSdkHeadlessMode] = useState(false);
  const { i18n } = useTranslation();

  const { isEmbedBySDK, isUsingDimensionChangeBySDK, parentOrigin } =
    useMemo(() => {
      const urlSearchParams = new URLSearchParams(window.location.search);

      return {
        isEmbedBySDK: !!urlSearchParams.get("sdk"),
        isUsingDimensionChangeBySDK:
          urlSearchParams.get("onDimensionsChange") === "true",
        parentOrigin: urlSearchParams.get("parent") || "*",
      };
    }, []);

  const parentWindow = useMemo(() => window.opener ?? window.parent, []);

  const messagingService = useMemo(
    () =>
      new MessagingService({
        target: parentWindow,
        targetOrigin: parentOrigin,
      }),
    [parentWindow, parentOrigin]
  );

  /**
   * listen to checkout embed result step
   * to send intentResult message to parent window
   * from the headless sdk injected in user browser
   */
  const replyToResultStep = useListenToResultStep({
    enabled: isEmbedBySDK,
    onResultStep: () => messagingService.send({ type: "intentResult" }),
  });

  useEffect(() => {
    messagingService.onReceive((event) => {
      const { type, data } = event.data || {};
      switch (type) {
        case "sdk:init": {
          if (data.styles) setStyles(data.styles as SDKContextType["styles"]);
          if (data.headless) setSdkHeadlessMode(data.headless as boolean);
          break;
        }

        case "changeLanguage": {
          const [lang] = (data.locale as string).split("-");
          i18n.changeLanguage(lang);
          // save lang in localStorage
          safeLocalStorage.setItem("mh__language__", lang);
          break;
        }

        case "EmbedResultClose": {
          replyToResultStep("EmbedResultClose");
          break;
        }

        case "SDKRenderUrl": {
          safeLocalStorage.setItem("SDKRenderUrl", "true");
          break;
        }

        default:
          break;
      }
    });

    return () => {
      messagingService.abortService();
    };
  }, [i18n, messagingService, replyToResultStep]);

  useEffect(() => {
    if (!isEmbedBySDK) return;
    messagingService.send({ type: "sdk:init" });

    if (isUsingDimensionChangeBySDK) {
      document.documentElement.style.height = "auto";

      const height = document.documentElement.scrollHeight;
      messagingService.send({
        type: "dimensionsChange",
        data: { iframeHeight: height },
      });

      const observer = new ResizeObserver(() => {
        const height = document.body.scrollHeight;
        messagingService.send({
          type: "dimensionsChange",
          data: { iframeHeight: height },
        });
      });

      observer.observe(document.body);
      return () => observer.disconnect();
    }
  }, [messagingService, isEmbedBySDK, isUsingDimensionChangeBySDK]);

  return (
    <SDKContext.Provider
      value={{
        messagingService,
        styles,
        sdkHeadlessMode,
        isEmbedBySDK,
        isUsingDimensionChangeBySDK,
      }}
    >
      {children}
    </SDKContext.Provider>
  );
}

export const useSDKContext = () => {
  const context = useContext(SDKContext);
  if (!context) {
    console.warn("useSDKContext must be used within <SDKContextProvider />");
  }
  return context;
};
